import React, { useMemo } from 'react';
import { builderPartners } from '../../data/mock';

/**
 * FloatingPartnerLogos
 *
 * Animated cluster of partner logos used in place of the static hero
 * image on /register-business. Logos drift gently in 2D using four
 * staggered keyframes (drift-1..4) defined in index.css, so neighbours
 * never move in sync.
 *
 * Reuses the same circular tile + cover/contain split used in
 * AllPartnersSection (builders cover the circle, pro-services
 * wordmarks sit padded inside).
 *
 * Layout: ~16 absolutely-positioned circles within a relative box.
 * Positions are hand-picked so the cluster reads as a balanced
 * "partner constellation" rather than overlapping clumps.
 */

const proPartners = [
  { name: 'JetFuel', logo: '/images/partners/jetfuel.png', kind: 'pro' },
  { name: 'HIA', logo: '/images/partners/hia.png', kind: 'pro' },
  { name: 'Homely', logo: '/images/partners/homely.png', kind: 'pro' },
  { name: 'Domain', logo: '/images/partners/domain.png', kind: 'pro' },
  { name: 'Houzz', logo: '/images/partners/houzz.png', kind: 'pro' },
  { name: 'Elite Agent', logo: '/images/partners/eliteagent.png', kind: 'pro' },
  { name: 'Loan Market Vantage', logo: '/images/partners/loanmarket.png', kind: 'pro' },
  { name: 'Equable', logo: '/images/partners/equable.png', kind: 'pro' },
  { name: 'Century 21', logo: '/images/partners/century21.jpg', kind: 'pro' },
  { name: 'Lanser', logo: '/images/partners/lanser.png', kind: 'pro' },
  { name: 'CPC Lawyers', logo: '/images/partners/cpclawyers.png', kind: 'pro' },
  { name: 'Ray White Prospect', logo: '/images/partners/raywhite.png', kind: 'pro' },
  { name: 'YPC Conveyancing', logo: '/images/partners/ypc.png', kind: 'pro' },
  { name: 'Plungie', logo: '/images/partners/plungie.png', kind: 'pro' },
  { name: 'PayLocker', logo: '/images/partners/paylocker-icon.png', kind: 'pro' },
];

const taggedBuilders = builderPartners.map((p) => ({ ...p, kind: 'builder' }));
const ALL = [...taggedBuilders, ...proPartners];

// Number of logos in the constellation. Tuned to fill the hero box
// generously without overcrowding.
const LOGO_COUNT = 16;

// Fisher-Yates shuffle - logos are mixed each load so visitors don't
// always see the same builder pinned top-left.
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Rejection-sample N positions inside a 100 x 100 normalized box so
// no two logos overlap. The container is roughly 4:3 in practice, so
// vertical distance is scaled by 0.75 when computing the no-overlap
// circle (otherwise logos clump vertically). Each logo also picks a
// random drift animation (1-4) and a random animation delay so the
// constellation looks freshly arranged on every load.
const generateCluster = (count, minSize, sizeVar, refWidthPx) => {
  const placed = [];
  const maxAttempts = 120;

  for (let i = 0; i < count; i++) {
    const size = minSize + Math.floor(Math.random() * sizeVar);
    const halfPct = ((size / refWidthPx) * 100) / 2 + 1.5; // +padding
    let pos = null;
    let attempt = 0;
    while (attempt < maxAttempts) {
      const left = halfPct + Math.random() * (100 - 2 * halfPct);
      const top = halfPct + Math.random() * (100 - 2 * halfPct);
      const ok = placed.every((p) => {
        const dx = p.left - left;
        const dy = (p.top - top) * 0.75; // y-compress for 4:3 container
        const dist = Math.sqrt(dx * dx + dy * dy);
        return dist > (p.halfPct + halfPct) * 1.05;
      });
      if (ok) { pos = { left, top, halfPct }; break; }
      attempt++;
    }
    // Fallback: if rejection sampling fails after maxAttempts, accept
    // a random position even if slightly overlapping. With 16 logos in
    // a 100x100 box this almost never fires.
    if (!pos) {
      pos = {
        left: halfPct + Math.random() * (100 - 2 * halfPct),
        top: halfPct + Math.random() * (100 - 2 * halfPct),
        halfPct,
      };
    }
    placed.push({
      ...pos,
      size,
      drift: 1 + Math.floor(Math.random() * 4),     // drift-1..4
      delay: `${(Math.random() * 3).toFixed(2)}s`,  // 0-3s offset
    });
  }
  return placed;
};

const FloatingPartnerLogos = ({
  heightClass = 'relative aspect-[4/3] lg:aspect-auto lg:h-[460px]',
  count = LOGO_COUNT,
  minSize = 56,
  sizeVar = 37,
  refWidthPx = 520,
}) => {
  const cluster = useMemo(() => {
    const picks = shuffleArray(ALL).slice(0, count);
    const positions = generateCluster(count, minSize, sizeVar, refWidthPx);
    return positions.map((pos, i) => ({ ...pos, partner: picks[i] }));
  }, [count, minSize, sizeVar, refWidthPx]);

  return (
    <div
      className={`w-full ${heightClass}`}
      data-testid="floating-partner-logos"
      aria-hidden="true"
    >
      {/* Soft radial wash so the cluster has depth without a hard frame */}
      <div
        className="absolute inset-0 rounded-3xl"
        style={{
          background:
            'radial-gradient(circle at 50% 50%, rgba(29,142,254,0.10) 0%, rgba(29,29,31,0) 70%)',
        }}
      />

      {cluster.map(({ partner, top, left, size, drift, delay }, i) => {
        const isBuilder = partner.kind === 'builder';
        return (
          <div
            key={`${partner.name}-${i}`}
            className={`absolute animate-drift-${drift}`}
            style={{
              top: `${top}%`,
              left: `${left}%`,
              width: `${size}px`,
              height: `${size}px`,
              // Center the logo on its (top,left) point using negative
              // margins rather than a CSS transform. The drift
              // animation also drives `transform`, so using transform
              // for centering here causes a one-off snap on first
              // animation frame.
              marginTop: `-${size / 2}px`,
              marginLeft: `-${size / 2}px`,
              animationDelay: delay,
              willChange: 'transform',
            }}
          >
            <div className="w-full h-full rounded-full overflow-hidden bg-white ring-1 ring-black/[0.06] shadow-[0_8px_24px_rgba(0,0,0,0.18)] flex items-center justify-center">
              <img
                src={partner.logo}
                alt=""
                aria-hidden="true"
                loading="lazy"
                className={isBuilder ? 'w-full h-full object-cover' : 'max-w-[78%] max-h-[78%] object-contain'}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FloatingPartnerLogos;
